import type { Month13 } from '@/lib/calendar13Types';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp } from 'lucide-react';
import { useTallRowExpand } from '@/lib/tallRowContext';

interface MonthHeaderProps {
  month: Month13;
  year: number;
  currentDay?: number;
  onPrevMonth: () => void;
  onNextMonth: () => void;
}

export function MonthHeader({
  month,
  year,
  currentDay,
  onPrevMonth,
  onNextMonth
}: MonthHeaderProps) {
  const tallRow = useTallRowExpand();
  const monthColorStyle = { color: `hsl(var(--month-${month.number}))` };

  return (
    <div className="mb-0.5 flex items-center justify-between gap-1 px-0 py-0">
      <Button
        variant="ghost"
        size="icon"
        onClick={onPrevMonth}
        className="h-6 w-6 min-w-6 rounded-full border border-white/8 bg-white/[0.04] text-white/68 hover:bg-white/[0.08] hover:text-white"
      >
        <ChevronLeft className="h-3 w-3" />
      </Button>

      <div className="text-center flex-1 min-w-0 flex items-center justify-center gap-2">
        <div className="flex items-center justify-center gap-1 min-w-0">
          <span className="text-[clamp(0.74rem,2vw,0.92rem)]" role="img" aria-label={month.name}>
            {month.glyph}
          </span>
          <h2
            className="truncate text-[clamp(0.8rem,2vw,0.95rem)] font-display font-semibold tracking-[0.04em]"
            style={monthColorStyle}
          >
            {month.name}
          </h2>
        </div>
        <span className="shrink-0 text-[0.65rem] font-mono tabular-nums text-white/35 tracking-tight border-l border-white/15 pl-2">
          Month {month.number} of 13
        </span>
      </div>

      <div className="flex items-center gap-0">
        {tallRow && (
          <Button
            variant="ghost"
            size="icon"
            onClick={tallRow.toggleTallExpanded}
            className="h-6 w-6 min-w-6 rounded-full border border-white/8 bg-white/[0.04] text-white/68 hover:bg-white/[0.08] hover:text-white"
          >
            {tallRow.isTallExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />}
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={onNextMonth}
          className="h-6 w-6 min-w-6 rounded-full border border-white/8 bg-white/[0.04] text-white/68 hover:bg-white/[0.08] hover:text-white"
        >
          <ChevronRight className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}
